import type { MatchLog, MatchSetup } from '@sd/bots';
import type { FactionId, MatchModeId, RichnessId } from '@sd/content';
import type { Command } from '@sd/sim';
import type { Snapshot } from './host';
import type { FromWorker, ToWorker } from './protocol';

/**
 * Network play (#139, ADR 0006): the server's rooms, as the client sees
 * them. These types mirror `packages/server/src/rooms.ts`.
 */
export type SeatControl = 'open' | 'human' | 'trader' | 'raider' | 'scientist' | 'cooperator';

export interface RoomSeat {
  readonly id: string;
  readonly faction: FactionId;
  readonly team?: number;
  readonly control: SeatControl;
  readonly account?: string;
  readonly online: boolean;
  /** A bot plays for this person while they are away (#142). */
  readonly standIn?: boolean;
}

export interface RoomView {
  readonly id: string;
  readonly name: string;
  readonly host: string;
  readonly visibility: 'public' | 'private';
  readonly status: 'lobby' | 'playing' | 'ended';
  readonly mode: MatchModeId;
  readonly richness: RichnessId;
  readonly findChoice: boolean;
  readonly botLevel: 'novice' | 'normal' | 'expert';
  readonly seats: readonly RoomSeat[];
  readonly members: readonly { readonly name: string; readonly online: boolean }[];
}

export interface RoomListing {
  readonly id: string;
  readonly name: string;
  readonly host: string;
  readonly seats: number;
  readonly people: number;
  readonly open: number;
}

export interface RoomSettings {
  readonly name?: string;
  readonly visibility?: 'public' | 'private';
  readonly players?: number;
  readonly mode?: MatchModeId;
  readonly richness?: RichnessId;
  readonly findChoice?: boolean;
  readonly botLevel?: 'novice' | 'normal' | 'expert';
  readonly seats?: readonly {
    readonly faction?: FactionId;
    readonly team?: number | null;
    readonly control?: SeatControl;
  }[];
}

/** What the server sends when a match starts, or to someone coming back to it. */
export interface MatchStart {
  readonly setup: MatchSetup;
  /** The seat of whoever receives it; null for someone without one. */
  readonly you: string | null;
  readonly log: MatchLog;
}

export interface NetStep {
  readonly s: number;
  readonly c?: readonly Command[];
}

/** An HTTP error from the server, with its reason («seat taken», «open seats»…). */
export class NetError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function call<T>(path: string, method = 'GET', body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: body === undefined ? {} : { 'content-type': 'application/json' },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const data = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new NetError(res.status, data.error ?? String(res.status));
  return data as T;
}

export const listRooms = () => call<RoomListing[]>('/api/rooms');
export const createRoom = (name: string, visibility: 'public' | 'private') =>
  call<RoomView>('/api/rooms', 'POST', { name, visibility });

export interface RoomHandlers {
  room(view: RoomView): void;
  start(start: MatchStart): void;
  step(step: NetStep): void;
  closed(): void;
  /** The connection dropped; the browser reconnects by itself. */
  offline(down: boolean): void;
}

/**
 * Being in a room: its events over Server-Sent Events, and the orders a
 * player can give it. One per room the player is in.
 */
export class RoomSession {
  private readonly source: EventSource;
  private queue: Command[] = [];
  private flushing = false;
  view: RoomView | null = null;

  constructor(
    readonly id: string,
    private readonly on: RoomHandlers,
  ) {
    this.source = new EventSource(`/api/rooms/${id}/events`, { withCredentials: true });
    const read = <T>(event: Event) => JSON.parse((event as MessageEvent<string>).data) as T;
    this.source.addEventListener('room', (e) => {
      this.view = read<RoomView>(e);
      this.on.room(this.view);
    });
    this.source.addEventListener('start', (e) => this.on.start(read<MatchStart>(e)));
    this.source.addEventListener('step', (e) => this.on.step(read<NetStep>(e)));
    this.source.addEventListener('closed', () => {
      this.source.close();
      this.on.closed();
    });
    this.source.addEventListener('open', () => this.on.offline(false));
    this.source.addEventListener('error', () => this.on.offline(true));
  }

  static join(id: string) {
    return call<RoomView>(`/api/rooms/${id}/join`, 'POST', {});
  }

  settings(patch: RoomSettings) {
    return call<RoomView>(`/api/rooms/${this.id}/settings`, 'POST', patch);
  }

  sit(index: number) {
    return call<RoomView>(`/api/rooms/${this.id}/seat`, 'POST', { index });
  }

  start() {
    return call<{ ok: true }>(`/api/rooms/${this.id}/start`, 'POST', {});
  }

  /** Orders go out together, one request at a time, so they arrive in order. */
  send(command: Command) {
    this.queue.push(command);
    this.schedule();
  }

  private schedule() {
    if (this.flushing || !this.queue.length) return;
    this.flushing = true;
    setTimeout(() => void this.flush(), 30);
  }

  private async flush() {
    const commands = this.queue;
    this.queue = [];
    try {
      await call(`/api/rooms/${this.id}/commands`, 'POST', { commands });
    } catch (error) {
      console.error('orders not sent', error);
    } finally {
      this.flushing = false;
      this.schedule();
    }
  }

  async leave() {
    this.source.close();
    await call(`/api/rooms/${this.id}/leave`, 'POST', {}).catch(() => null);
  }

  close() {
    this.source.close();
  }
}

/**
 * The page's handle on a network match: the same as `MatchHost`, but the
 * worker steps what the server sends instead of keeping its own time.
 */
export class NetHost {
  private readonly worker = new Worker(new URL('./sim.worker.ts', import.meta.url), {
    type: 'module',
  });
  private pendingLog: ((log: MatchLog) => void) | null = null;

  constructor(
    start: MatchStart,
    private readonly session: RoomSession,
    onSnapshot: (snapshot: Snapshot) => void,
    onError: (message: string) => void,
  ) {
    this.worker.addEventListener('message', (event: MessageEvent<FromWorker>) => {
      const message = event.data;
      if (message.type === 'snapshot') onSnapshot(message);
      else if (message.type === 'log') this.pendingLog?.(message.log);
      else onError(message.message);
    });
    this.worker.addEventListener('error', (event) => onError(event.message));
    this.post({ type: 'remote', setup: start.setup, log: start.log });
  }

  /** The whole match again, after the connection came back: the log replaces what was here. */
  resync(start: MatchStart) {
    this.post({ type: 'remote', setup: start.setup, log: start.log });
  }

  /** A step from the server, in order. */
  step(step: NetStep) {
    this.post({ type: 'steps', steps: [step] });
  }

  send(command: Command) {
    this.session.send(command);
  }

  /** Time is the server's: a menu does not stop it. */
  freeze() {}

  setSpeed() {}

  log(): Promise<MatchLog> {
    return new Promise((resolve) => {
      this.pendingLog = resolve;
      this.post({ type: 'log' });
    });
  }

  dispose() {
    this.worker.terminate();
  }

  private post(message: ToWorker) {
    this.worker.postMessage(message);
  }
}
